import { Group, UnstyledButton } from '@mantine/core';
import { CSSProperties, ReactElement, ReactNode } from 'react';
import { QueueFilterMenu, QueueFilterMenuProps } from './QueueFilterMenu';
import './QueueFilterBar.css';

/**
 * The hub's filter bar: the date first, then the same four filters on every tab.
 *
 * Reset sits at the right-hand end and appears only when something is off its
 * default, so it doubles as the answer to "why is this list short".
 */
export interface QueueFilterBarProps {
  /** The tab's `ListRangeControl`, labelled for what the date means there; absent on a list with no date (Receivables). */
  date?: ReactNode;
  /** In display order: PN, Payment track, Location, Status. */
  filters: QueueFilterMenuProps[];
  isFiltered: boolean;
  onReset: () => void;
  /** After the filters: a tab's chips. */
  extras?: ReactNode;
  /** At the right-hand end, after Reset: a tab's buttons. */
  actions?: ReactNode;
}

const BAR_STYLE: CSSProperties = {
  padding: '10px 12px',
  background: '#fff',
  borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
};

const RESET_STYLE: CSSProperties = {
  marginLeft: 'auto',
  padding: '6px 10px',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  color: 'var(--ev-color-green-dark, #243721)',
};

export function QueueFilterBar({
  date,
  filters,
  isFiltered,
  onReset,
  extras,
  actions,
}: QueueFilterBarProps): ReactElement {
  return (
    <Group gap={8} wrap="wrap" style={BAR_STYLE}>
      {date}
      {filters.map((filter) => (
        <QueueFilterMenu key={filter.label} {...filter} />
      ))}
      {extras}
      {isFiltered && (
        <UnstyledButton className="queue-filter-reset" style={RESET_STYLE} onClick={onReset}>
          Reset filters
        </UnstyledButton>
      )}
      {actions && (
        <Group gap={8} wrap="nowrap" ml={isFiltered ? undefined : 'auto'}>
          {actions}
        </Group>
      )}
    </Group>
  );
}
